import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { EximiblesDTO, EximirEnSolicitud } from "../../models/ExencionDTO";
import "./rotativaPicker.css";

export interface EximirMesesPickerProps {
	campusId: number;
	scheduleId: number;
	/** null = no exime meses. */
	value: EximirEnSolicitud | null;
	onChange: (value: EximirEnSolicitud | null) => void;
	/** La ficha del cliente (si ya existe): el servidor dice si es reingreso. */
	studentId: number | null;
	disabled?: boolean;
	marcar?: boolean;
}

const http = HttpManager.getInstance();
const MIN_EXPLICACION = 10;

/**
 * Eximir meses (Esteban, 2026-10-28): quien entra a un horario ya avanzado por reingreso o por examen de nivelación (solo Inglés)
 * no paga los meses de antes. Se eximen al aprobar la Matrícula, antes de la promoción: «1ra mensualidad» cae en el mes real de
 * entrada. Solo sale si el horario ya empezó y hay un motivo que le toque (Reingreso solo a clientes de reingreso, Nivelación solo en
 * Inglés), o si ya trae meses guardados.
 */
const EximirMesesPicker: React.FC<EximirMesesPickerProps> = ({ campusId, scheduleId, value, onChange, studentId, disabled, marcar }) => {
	const [data, setData] = useState<EximiblesDTO | null>(null);
	const activa = value !== null;

	useEffect(() => {
		let vigente = true;
		setData(null);
		http.enrollmentRequestsService.eximibles(campusId, scheduleId, studentId)
			.then((r) => vigente && setData(r.data))
			.catch(() => vigente && setData(null));
		return () => { vigente = false; };
	}, [campusId, scheduleId, studentId]);

	if (!data || ((!data.started || data.reasons.length < 1) && !activa)) return null;

	const v: EximirEnSolicitud = value || { months: [], reason: "", explanation: "" };
	const alternar = () => { if (!disabled) onChange(activa ? null : { months: [], reason: "", explanation: "" }); };
	const elegirMes = (ym: string) => {
		if (disabled || !activa) return;
		onChange({ ...v, months: v.months.includes(ym) ? v.months.filter((x) => x !== ym) : [...v.months, ym].sort() });
	};
	const motivos = data.reasons.map((r) => ({ key: r.key, name: r.key === "nivelacion" ? "Examen de Nivelación" : r.name }));
	const motivoViejo = v.reason !== "" && !motivos.some((m) => m.key === v.reason);
	const elegibles = data.months.filter((m) => m.allowed);
	const primeroQuePaga = data.months.find((m) => !v.months.includes(m.ym));
	const faltaMeses = v.months.length < 1;
	const faltaMotivo = v.reason === "";
	const faltaExplicacion = v.explanation.trim().length < MIN_EXPLICACION;

	return (
		<div className="waRotativa">
			<div className="waRotativaFila" onClick={alternar}>
				<div>
					<b>Eximir Meses</b>
					<span>Reingreso o examen de nivelación: no paga los meses antes de entrar</span>
				</div>
				<button type="button" role="switch" aria-checked={activa} aria-label="Eximir Meses" className="waInterruptor" disabled={disabled}
					onClick={(e) => { e.stopPropagation(); alternar(); }} />
			</div>
			{activa ? (
				<>
					<div className="waRotativaOpciones">
						{motivos.map((m) => (
							<button key={"mot_" + m.key} type="button" className="waPildora" aria-pressed={v.reason === m.key} disabled={disabled}
								onClick={() => onChange({ ...v, reason: m.key })}>
								{m.name}
							</button>
						))}
					</div>
					{motivos.length < 1 ? <div className="waRotativaResumen falta">Este cliente no es reingreso y el curso no es Inglés: quita Eximir Meses.</div>
						: motivoViejo ? <div className="waRotativaResumen falta">El motivo guardado ya no le toca a este cliente: elige otro.</div>
						: marcar && faltaMotivo ? <div className="waRotativaResumen falta">Elige el motivo.</div> : null}
					<div className="waRotativaOpciones">
						{elegibles.map((m) => (
							<button key={"mes_" + m.ym} type="button" className="waPildora waPildoraMes" aria-pressed={v.months.includes(m.ym)} disabled={disabled}
								onClick={() => elegirMes(m.ym)}>
								{m.title}
								<small>Mes {m.index}</small>
							</button>
						))}
					</div>
					<textarea className="form-control" rows={2} style={{ marginTop: 10 }} placeholder="Explicación (va a Auditoría)" value={v.explanation} disabled={disabled}
						onChange={(e) => onChange({ ...v, explanation: e.target.value })} />
					<div className={"waRotativaResumen" + (marcar && (faltaMeses || faltaExplicacion) ? " falta" : "")}>
						{faltaMeses
							? "Toca los meses que no paga."
							: `No paga ${v.months.length} ${v.months.length === 1 ? "mes" : "meses"}` + (primeroQuePaga ? ` · empieza a pagar en ${primeroQuePaga.title}` : "")
								+ (faltaExplicacion ? " · falta la explicación" : "")}
					</div>
				</>
			) : null}
		</div>
	);
};

export default EximirMesesPicker;
